import React, { useMemo } from "react";
import { changeTypeMeta } from "./refreshMeta";

/**
 * The AI's own account of a content refresh: a one-line summary plus itemised
 * changes, each with the data that motivated it.
 *
 * This is SELF-REPORTED and deliberately framed as such in the UI. The model
 * can describe an edit it did not fully make, or omit one it did, so this sits
 * beside the diff as a reading aid — never as a substitute for it.
 *
 * Gap-coverage claims are cross-checked against `refreshSignals.gapQueries`
 * (the queries Search Console actually recorded). A claim naming no known gap
 * query is flagged rather than hidden: an unverifiable claim is exactly what a
 * reviewer needs to look at hardest.
 */
const RefreshRationale = ({ post }) => {
  const summary = post?.refreshSummary;
  const changes = useMemo(
    () => (Array.isArray(post?.refreshChanges) ? post.refreshChanges : []),
    [post?.refreshChanges]
  );
  const gapQueries = useMemo(
    () => post?.refreshSignals?.gapQueries || [],
    [post?.refreshSignals?.gapQueries]
  );

  // Match on substring both ways: the model tends to quote a gap query inside a
  // fuller sentence, but may also paraphrase a long query down to its head term.
  const verified = useMemo(() => changes.map((c) => {
    if (c.type !== "gap_coverage") return null;
    if (!gapQueries.length) return null;
    const haystack = `${c.what} ${c.why}`.toLowerCase();
    return gapQueries.some((q) => {
      const needle = String(q).toLowerCase().trim();
      return needle.length > 0 && (haystack.includes(needle) || needle.includes(haystack));
    });
  }), [changes, gapQueries]);

  // A refresh with no rationale used to render nothing, which read as "the AI
  // had nothing to say" when the real cause was a generation failure. Surface
  // the gap instead — a reviewer approving an unexplained rewrite of a page
  // that already ranks needs to know the explanation is missing, not absent.
  if (!summary && changes.length === 0) {
    return (
      <div className="max-w-[860px] mx-auto px-4 pt-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
          <p className="text-sm font-semibold text-amber-800 mb-1">
            ⚠️ No rationale available for this refresh
          </p>
          <p className="text-xs text-amber-700 leading-relaxed">
            {post?.truncated
              ? "The AI response hit its output limit before it recorded what it changed. Review the diff carefully — the article ending may also be incomplete."
              : "The AI did not return a summary of its changes. Review the diff on its own merits before approving."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[860px] mx-auto px-4 pt-6">
      <div className="bg-white border border-indigo-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-2 flex-wrap">
          <span className="text-indigo-700 font-semibold text-sm">🤖 What the AI changed &amp; why</span>
          <span className="ml-auto text-xs text-indigo-400">
            Self-reported — verify against the diff
          </span>
        </div>

        <div className="p-5 space-y-4">
          {summary && (
            <p className="text-sm text-gray-800 leading-relaxed">{summary}</p>
          )}

          {changes.length > 0 && (
            <ul className="space-y-3">
              {changes.map((change, i) => {
                const meta = changeTypeMeta(change.type);
                const isVerified = verified[i];
                return (
                  <li
                    key={i}
                    className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.className}`}>
                        {meta.icon} {meta.label}
                      </span>
                      {change.where && (
                        <span className="text-xs text-gray-400">in {change.where}</span>
                      )}
                      {isVerified === true && (
                        <span
                          className="text-xs text-emerald-600"
                          title="Names a query from this post's Search Console gap list"
                        >
                          ✓ matches a tracked gap query
                        </span>
                      )}
                      {isVerified === false && (
                        <span
                          className="ml-auto text-xs text-amber-600"
                          title="No query in this post's Search Console gap list matches this claim — check the diff"
                        >
                          ⚠ unmatched claim
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-900">{change.what}</p>
                    {change.why && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium text-gray-500">Why: </span>
                        {change.why}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Older drafts predate refreshChanges — say so rather than looking broken. */}
          {summary && changes.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              This draft predates itemised change tracking — only the summary is available.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefreshRationale;
